import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Database, ArrowRight, Receipt, Calendar, Users, Settings, DollarSign, Clock, Award, Briefcase, Bug } from 'lucide-react';

const ToolCard = ({ icon: Icon, title, description, to, color }) => (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
        <div className="card" style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '2rem',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            borderTop: `4px solid ${color}`
        }}>
            <div style={{
                background: `${color}20`,
                width: 'fit-content',
                padding: '1rem',
                borderRadius: '1rem',
                marginBottom: '1.5rem',
                color: color
            }}>
                <Icon size={32} />
            </div>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>{title}</h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', flex: 1 }}>{description}</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: color, fontWeight: 600 }}>
                Open Tool <ArrowRight size={16} />
            </div>
        </div>
    </Link>
);

import api from '../services/api';

const Dashboard = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isAdmin = user.role === 'ADMIN' || user.role === 'HR';
    const [config, setConfig] = React.useState({ orgName: 'PATHtoIP' });

    React.useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await api.get('/auth/config');
                setConfig(response.data);
            } catch (err) {
                console.error('Failed to fetch dashboard config', err);
            }
        };
        fetchConfig();
    }, []);

    return (
        <div className="container section">
            <div style={{ maxWidth: '800px', margin: '0 auto 3rem', textAlign: 'center' }}>
                <h1 className="gradient-text" style={{ fontSize: '3rem', marginBottom: '1rem' }}>{config.orgName} Tool Lab</h1>
                <p style={{ fontSize: '1.2rem', color: 'var(--text-muted)' }}>
                    Access our suite of professional tools. Select a category below to get started.
                </p>
                {user.name && <p className="mt-4 text-gray-500">Welcome back, <strong>{user.name}</strong></p>}
            </div>

            {/* Category 1: Analysis and Productivity */}
            <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 pl-4 border-l-4 border-blue-600">Analysis and Productivity</h2>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '2rem',
                    padding: '1rem'
                }}>
                    <ToolCard
                        icon={FileText}
                        title="PDF Tools"
                        description="Comprehensive suite for PDF manipulation: Merge, Split, Convert, and more."
                        to="/tools/pdf"
                        color="#4f46e5"
                    />
                    <ToolCard
                        icon={Database}
                        title="Data Analyzer"
                        description="Advanced data visualization and exploration powered by Apache Superset."
                        to="/tools/dummy1"
                        color="#10b981"
                    />
                </div>
            </div>

            {/* Category 2: Employee Administration */}
            <div className="mt-12">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 pl-4 border-l-4 border-purple-600">Employee Administration</h2>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '2rem',
                    padding: '1rem'
                }}>
                    <ToolCard
                        icon={Receipt}
                        title="Expense Reports"
                        description="Create expense reports with OCR receipt scanning and PDF export."
                        to="/tools/expenses"
                        color="#8b5cf6"
                    />
                    <ToolCard
                        icon={Calendar}
                        title="Leave Management"
                        description="Track leave balances, apply for time off, and view team calendar."
                        to="/leaves"
                        color="#f59e0b"
                    />
                    <ToolCard
                        icon={DollarSign}
                        title="My Salary"
                        description="View your salary history and download slips."
                        to="/my-salary"
                        color="#16a34a"
                    />
                    <ToolCard
                        icon={Clock}
                        title="Attendance"
                        description="Mark your daily attendance and view your presence history."
                        to="/attendance"
                        color="#06b6d4"
                    />
                </div>
            </div>

            {/* Category 3: Company Administration */}
            {isAdmin && (
                <div className="mt-12">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 pl-4 border-l-4 border-gray-800">Company Administration</h2>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '2rem',
                        padding: '1rem'
                    }}>
                        <ToolCard
                            icon={Users}
                            title="HRMS"
                            description="Onboard users, manage roles, and view organizational hierarchy."
                            to="/admin/users"
                            color="#374151"
                        />
                        <ToolCard
                            icon={DollarSign}
                            title="Payroll Management"
                            description="Calculate monthly salaries, handle unpaid leaves, and generate reports."
                            to="/admin/payroll"
                            color="#374151"
                        />
                        <ToolCard
                            icon={Calendar}
                            title="Holiday Management"
                            description="Upload and manage company holiday calendar for all employees."
                            to="/admin/holidays"
                            color="#374151"
                        />
                        <ToolCard
                            icon={Settings}
                            title="Workflow Configuration"
                            description="Configure leave approval workflows and email notifications."
                            to="/admin/workflow"
                            color="#374151"
                        />
                        {user.role === 'ADMIN' && (
                            <ToolCard
                                icon={Settings}
                                title="System Configuration"
                                description="Configure system settings, integrations (Google OAuth), and API keys."
                                to="/admin/config"
                                color="#374151"
                            />
                        )}
                        <ToolCard
                            icon={Clock}
                            title="Attendance Management"
                            description="Monitor employee attendance and working hours."
                            to="/admin/attendance"
                            color="#374151"
                        />
                    </div>
                </div>
            )}

            {/* Support & Feedback Section - Compact */}
            <div style={{
                marginTop: '4rem',
                borderTop: '1px solid #e5e7eb',
                paddingTop: '2rem',
                display: 'flex',
                justifyContent: 'center',
                marginBottom: '1rem'
            }}>
                <a 
                    href="https://junkyardamoeba.atlassian.net/jira/core/form/8b6c4613-001e-4064-ae0a-a499fcec0cb2" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        color: '#ef4444',
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        background: 'rgba(239, 68, 68, 0.05)',
                        transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                    }}
                >
                    <Bug size={16} /> Report a Bug or Suggestion
                </a>
            </div>
        </div>
    );
};

export default Dashboard;
